import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import LocalDate from '../../components/common/local-date.tsx';
import copyTextToClipboard from '../../utils/copy-text-to-clipboard.ts';
import formatApiMessage from '../../utils/format-api-message.ts';
import { RoutesPermissionEnum as RP } from '../../utils/enums.ts';

type ImportUploadSummary = {
  phase: 'Staged' | 'Applied';
  clipboardText: string;
  appliedAt?: string;
  errors?: Array<{
    rowNumber: number;
    reference: string;
    messages: unknown[];
  }>;
};

type ImportUpload = {
  id: number;
  fileName: string;
  fileUrl?: string;
  worksheetName?: string;
  totalRows: number;
  insertedCount: number;
  updatedCount: number;
  skippedCount: number;
  errorCount: number;
  createdAt: string;
  summary: ImportUploadSummary;
};

const getWorkbookErrorMessage = (
  errors?: Array<{
    rowNumber: number;
    reference: string;
    messages: unknown[];
  }>,
) => {
  const workbookError = errors?.find((item) => item.rowNumber === 0);

  if (!workbookError?.messages?.length) {
    return '';
  }

  return formatApiMessage(workbookError.messages);
};

const DataUploadHistory = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [uploads, setUploads] = useState<ImportUpload[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadUploads = async () => {
      setIsLoading(true);
      setError('');

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/question/import-uploads`,
          {
            headers: {
              authorization: `Bearer ${token}`,
            },
          },
        );
        const data = (await response.json().catch(() => null)) as
          | ImportUpload[]
          | { message?: unknown }
          | null;

        if (!response.ok) {
          const message =
            data &&
            !Array.isArray(data) &&
            typeof data === 'object' &&
            'message' in data
              ? formatApiMessage(data.message)
              : '';
          throw new Error(message || 'Unable to load upload history.');
        }

        setUploads(Array.isArray(data) ? data : []);
      } catch (loadError) {
        const message =
          loadError instanceof Error
            ? loadError.message
            : 'Unable to load upload history.';
        setError(message);
        toast.error(message);
      } finally {
        setIsLoading(false);
      }
    };

    loadUploads();
  }, [getAccessTokenSilently]);

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <section className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1>Upload History</h1>
          <p className="description">
            Review staged uploads and revisit applied ones.
          </p>
        </div>
        <Link className="link-blue-button" to={RP.DATA_UPLOAD}>
          Back to Upload
        </Link>
      </section>

      {isLoading && (
        <section className="rounded-lg border border-table-border bg-white p-6">
          <p className="text-sm text-dark-gray">Loading uploads...</p>
        </section>
      )}

      {!isLoading && error && (
        <section className="rounded-lg border border-[#f5c2c7] bg-[#fff5f5] p-6">
          <p className="text-sm text-[#b42318]">{error}</p>
        </section>
      )}

      {!isLoading && !error && !uploads.length && (
        <section className="rounded-lg border border-table-border bg-white p-6">
          <p className="text-sm text-dark-gray">No uploads yet.</p>
        </section>
      )}

      {!isLoading &&
        !error &&
        uploads.map((upload) => {
          const isStaged = upload.summary?.phase === 'Staged';
          const workbookErrorMessage = getWorkbookErrorMessage(
            upload.summary?.errors,
          );
          const reviewHref = RP.DATA_UPLOAD_HISTORY_UPLOAD_ID.replace(
            ':uploadId',
            String(upload.id),
          );

          return (
            <article
              className="rounded-lg border border-table-border bg-white p-6"
              key={upload.id}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-dark-blue">
                    <LocalDate
                      date={upload.createdAt}
                      format="yyyy/MM/dd '|' p"
                    />
                  </h2>
                  <p className="mt-1 text-sm text-dark-gray">{upload.fileName}</p>
                  {upload.worksheetName && (
                    <p className="mt-1 text-sm text-dark-gray">
                      Sheet: {upload.worksheetName}
                    </p>
                  )}
                  <p className="mt-2 text-sm text-dark-blue">
                    Status: {upload.summary?.phase || 'Staged'}
                  </p>
                  {!!upload.summary?.appliedAt && (
                    <p className="mt-1 text-sm text-dark-gray">
                      Applied on{' '}
                      <LocalDate
                        date={upload.summary.appliedAt}
                        format="yyyy/MM/dd '|' p"
                      />
                    </p>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  <Link className="link-blue-button" to={reviewHref}>
                    Review
                  </Link>
                  {upload.fileUrl && (
                    <a
                      className="rounded-lg border border-blue px-4 py-2 text-sm font-medium text-blue"
                      href={upload.fileUrl}
                      rel="noreferrer"
                      target="_blank"
                    >
                      Workbook
                    </a>
                  )}
                  {!isStaged && upload.summary?.clipboardText && (
                    <button
                      className="rounded-lg border border-blue px-4 py-2 text-sm font-medium text-blue"
                      onClick={() => copyTextToClipboard(upload.summary.clipboardText)}
                      type="button"
                    >
                      Copy IDs
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-3 text-sm text-dark-gray">
                <span>{upload.totalRows} rows</span>
                <span>{upload.insertedCount} {isStaged ? 'would add' : 'added'}</span>
                <span>{upload.updatedCount} {isStaged ? 'would update' : 'updated'}</span>
                <span>{upload.skippedCount} {isStaged ? 'would skip' : 'skipped'}</span>
                <span>{upload.errorCount} {isStaged ? 'would error' : 'errors'}</span>
              </div>

              {!!workbookErrorMessage && (
                <div className="mt-4 rounded-lg border border-[#f5c2c7] bg-[#fff5f5] p-4">
                  <p className="text-sm font-medium text-[#b42318]">
                    {workbookErrorMessage}
                  </p>
                </div>
              )}
            </article>
          );
        })}
    </div>
  );
};

export default DataUploadHistory;
